import { MutableRecordStore } from '@deqo/foundation';
import { useQuery } from '@tanstack/react-query';
import { shade, tint } from 'polished';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors, Input } from '../../../ui';
import { FlexScroller, ScrollerInteral } from '../../../ui/Scrollers';
import { useResolve } from '../../Container';
import { Popover } from '../../canvas/ui/Popover';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { Chat } from './Chat';
import { GeneralInteraction } from './interactions/GeneralInteraction';
import { JiraCreateInteraction } from './interactions/JiraCreateInteraction';
import { ProductHelpInteraction } from './interactions/ProductHelpInteraction';

const ButtonFrame = styled.div<{ active: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: ${tint(.1, Colors.positive)};
    color: white;
    cursor: pointer;
    user-select: none;

    &:hover {
        background: ${Colors.positive};
    }
    ${p => p.active && css`
        background: ${Colors.positive};
    `}
    &:active {
        background: ${shade(.2, Colors.positive)};
    }
`;

const useCapabilityQuery = (message: string) => {

    const api = useResolve(ApiClient);

    const { data } = useQuery({
        queryKey: ['llm.selectCapability', message],
        queryFn: () => api.exec(v2.llm.selectCapability(message)),
    });

    return data;
};

const Thinking = () => {
    return <Chat.Block actor={<Chat.Seb />}>
        <Chat.Message>
            Thinking...
        </Chat.Message>
    </Chat.Block>;
};

const Interaction = ({ input }: { input: string }) => {
    const { code } = useCapabilityQuery(input) ?? {};
    return <>
        <Chat.Block actor={<Chat.You />}>
            <Chat.Message pre>{input}</Chat.Message>
        </Chat.Block>
        {!code && <Thinking />}
        {code?.includes('general') && <GeneralInteraction input={input} />}
        {code?.includes('jira_create') && <JiraCreateInteraction input={input} />}
        {code?.includes('product_help') && <ProductHelpInteraction input={input} />}
    </>;
};

export class AssistantState extends MutableRecordStore({ tick: 0 }) { }
export const AssistantContext = createContext<{ state: AssistantState, onSubmit: (input: string) => void }>(null);

const AssistantAutoFlexScroller = ({ children }: { children: React.ReactNode }) => {
    const scroller = useRef<ScrollerInteral>();
    const { state } = useContext(AssistantContext);
    const { tick } = useObserver(state);

    useEffect(() => {
        const se = scroller.current?.getScrollElement();
        if (se) {
            se.scrollTo(0, se.scrollHeight);
        }
    }, [tick]);

    return <FlexScroller internalRef={scroller}>
        {children}
    </FlexScroller>;
};

const AssitantWidget = () => {

    const [messages, setMessages] = useState([] as string[]);
    const [input, setInput] = useState('');

    const onSubmit = (value: string) => {
        setMessages([...messages, value]);
        setInput('');
    };

    const [state] = useState(() => new AssistantState());

    return <AssistantContext.Provider value={{ state, onSubmit }}>
        <Flexer flex="column" gap="1" width="40" height="48" padding="1">
            <Flexer flex="column" grow>
                <AssistantAutoFlexScroller>
                    <Chat>
                        <Chat.Block actor={<Chat.Seb />}>
                            <Chat.Message>
                                Hey there, how can I help you today?
                            </Chat.Message>
                            <Chat.Message>
                                To see a list of my capabilities, just ask me.
                            </Chat.Message>
                        </Chat.Block>
                        {messages.map((m, i) => <Interaction key={i} input={m} />)}
                    </Chat>
                </AssistantAutoFlexScroller>
            </Flexer>
            <Flexer>
                <Chat>
                    <Chat.Block actor={<Chat.You />}>
                        <Input multiline={{ resize: 'none', rows: 3 }} value={input} onChange={setInput} onApply={onSubmit} />
                    </Chat.Block>
                </Chat>
            </Flexer>
        </Flexer>
    </AssistantContext.Provider>;
};

export const AssistantButtonWidget = () => {

    const [open, setOpen] = useState(false);

    return <Flexer margin="0 0 8 0" pointerEvents="none">
        <Flexer pointerEvents="all">
            <Popover
                placement="left-end"
                content={<AssitantWidget />}
                inner={{ direction: 'left', offset: '1' }}
                open={open}
                onOpenChange={setOpen}
                promptOpts={{
                    ignoreEscape: true,
                    ignoreMouse: true,
                }}
            >
                <ButtonFrame active={false}>
                    <Icon size="2" src="deqo://icons/hero/chip" />
                </ButtonFrame>
            </Popover>
        </Flexer>
    </Flexer>;
};
